'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { useT } from '@/components/i18n-provider';
import { Markdown } from './markdown';
import { getLanguage, isRenderableDocument } from '@/lib/content/language-map';
import { Download, ExternalLink, Copy } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { useContentSubscription } from '@/lib/content/use-content-subscription';

interface ContentViewerProps {
  contentUrl: string;
  mimeType: string;
  filename?: string;
  language?: string;
  className?: string;
  maxHeight?: string;
  /**
   * When provided, subscribe to content.updated broadcasts for this id and
   * refetch the rendered text on every event. Lets an open lightbox/preview
   * follow live edits made by sub-agents in delegate sub-chats — broadcast
   * routing in lib/ws/server.ts is content-id scoped, so the event reaches
   * us regardless of which chat the editor was in.
   */
  contentId?: string;
}

export function ContentViewer({
  contentUrl,
  mimeType,
  filename,
  language: languageOverride,
  className = '',
  maxHeight,
  contentId,
}: ContentViewerProps) {
  const base = mimeType.split(';')[0].trim().toLowerCase();
  const [refreshKey, setRefreshKey] = useState(0);

  useContentSubscription(contentId ?? null, () => {
    setRefreshKey((k) => k + 1);
  });

  if (base === 'application/pdf') {
    return <PDFViewer url={contentUrl} className={className} maxHeight={maxHeight} />;
  }

  if (base === 'text/markdown') {
    return <MarkdownViewer url={contentUrl} className={className} maxHeight={maxHeight} refreshKey={refreshKey} />;
  }

  const lang = languageOverride || getLanguage(mimeType, filename);
  if (lang && lang !== 'plaintext' && lang !== 'markdown') {
    return <CodeViewer url={contentUrl} language={lang} className={className} maxHeight={maxHeight} refreshKey={refreshKey} />;
  }

  if (base.startsWith('text/') || base === 'application/json') {
    return <PlainTextViewer url={contentUrl} className={className} maxHeight={maxHeight} refreshKey={refreshKey} />;
  }

  return <DownloadCard url={contentUrl} filename={filename} mimeType={mimeType} />;
}

function PDFViewer({ url, className, maxHeight }: { url: string; className?: string; maxHeight?: string }) {
  const t = useT();
  return (
    <div className={`relative ${className}`} style={{ height: maxHeight || '80vh' }}>
      <iframe
        src={url}
        className="w-full h-full border-0 rounded-md"
        title={t('postMedia.pdfDocumentTitle')}
      />
      <div className="absolute top-2 right-2 flex gap-1">
        <a href={url} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-background/80 hover:bg-background rounded-md border shadow-sm">
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}

function MarkdownViewer({ url, className, maxHeight, refreshKey }: { url: string; className?: string; maxHeight?: string; refreshKey?: number }) {
  const { text, loading, error } = useTextFetch(url, refreshKey);

  if (loading) return <ViewerSkeleton />;
  if (error || text === null) return <DownloadCard url={url} mimeType="text/markdown" />;

  return (
    <div className={`overflow-auto prose prose-sm dark:prose-invert max-w-none p-4 ${className}`} style={maxHeight ? { maxHeight } : undefined}>
      <Markdown>{text}</Markdown>
    </div>
  );
}

function CodeViewer({ url, language, className, maxHeight, refreshKey }: { url: string; language: string; className?: string; maxHeight?: string; refreshKey?: number }) {
  const { text, loading, error } = useTextFetch(url, refreshKey);
  const [html, setHtml] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!text) return;
    let cancelled = false;
    (async () => {
      try {
        const { codeToHtml } = await import('shiki');
        const highlighted = await codeToHtml(text, {
          lang: language,
          theme: 'github-dark',
        });
        if (!cancelled) setHtml(highlighted);
      } catch {
        if (!cancelled) setHtml(null);
      }
    })();
    return () => { cancelled = true; };
  }, [text, language]);

  if (loading) return <ViewerSkeleton />;
  if (error || text === null) return <DownloadCard url={url} mimeType="text/plain" />;

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!html) {
    return (
      <div className={`relative ${className}`}>
        <div className="absolute top-2 right-2 z-10">
          <button onClick={handleCopy} className="p-1.5 bg-background/80 hover:bg-background rounded-md border shadow-sm text-xs">
            {copied ? 'Copied!' : <Copy className="h-3.5 w-3.5" />}
          </button>
        </div>
        <pre className="overflow-auto bg-muted/50 rounded-md p-4 text-sm font-mono" style={maxHeight ? { maxHeight } : undefined}>
          {addLineNumbers(text)}
        </pre>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div className="absolute top-2 right-2 z-10 flex gap-1">
        <span className="px-2 py-0.5 bg-background/80 rounded-md border text-xs text-muted-foreground">{language}</span>
        <button onClick={handleCopy} className="p-1.5 bg-background/80 hover:bg-background rounded-md border shadow-sm text-xs">
          {copied ? 'Copied!' : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
      <div
        className="overflow-auto rounded-md text-sm [&_pre]:p-4 [&_pre]:!bg-[#0d1117]"
        style={maxHeight ? { maxHeight } : undefined}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

function PlainTextViewer({ url, className, maxHeight, refreshKey }: { url: string; className?: string; maxHeight?: string; refreshKey?: number }) {
  const { text, loading, error } = useTextFetch(url, refreshKey);

  if (loading) return <ViewerSkeleton />;
  if (error || text === null) return <DownloadCard url={url} mimeType="text/plain" />;

  return (
    <pre className={`overflow-auto bg-muted/50 rounded-md p-4 text-sm font-mono whitespace-pre-wrap ${className}`} style={maxHeight ? { maxHeight } : undefined}>
      {addLineNumbers(text)}
    </pre>
  );
}

function DownloadCard({ url, filename, mimeType }: { url: string; filename?: string; mimeType?: string }) {
  return (
    <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/30">
      <Download className="h-8 w-8 text-muted-foreground shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{filename || 'Download file'}</p>
        {mimeType && <p className="text-xs text-muted-foreground">{mimeType}</p>}
      </div>
      <a href={url} download className="px-3 py-1.5 bg-primary text-primary-foreground text-sm rounded-md hover:bg-primary/90">
        Download
      </a>
    </div>
  );
}

function ViewerSkeleton() {
  return (
    <div className="flex items-center justify-center p-8 bg-muted/30 rounded-md">
      <Spinner size="lg" tone="muted" />
    </div>
  );
}

function useTextFetch(url: string, refreshKey?: number) {
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    // Bust the cache when re-fetching after a content.updated broadcast —
    // signed content URLs are otherwise cached by the browser and would
    // serve a stale body. The cache buster is keyed on refreshKey so
    // first-render fetches still hit the cache.
    const fetchUrl = (refreshKey ?? 0) > 0
      ? `${url}${url.includes('?') ? '&' : '?'}_r=${refreshKey}`
      : url;
    fetch(fetchUrl, { cache: (refreshKey ?? 0) > 0 ? 'no-store' : 'default' })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((t) => { if (!cancelled) { setText(t); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [url, refreshKey]);

  return { text, loading, error };
}

function addLineNumbers(text: string): string {
  const lines = text.split('\n');
  const pad = String(lines.length).length;
  return lines.map((line, i) => `${String(i + 1).padStart(pad)}  ${line}`).join('\n');
}

export { isRenderableDocument };
